import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  ConfigDeployment,
  DeployBuildVersionModal,
  DetailItem,
  LegacyDetailsList,
  useToast,
} from '@bymason-private/controller-shared-widgets';
import { makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { Button } from '@bymason-private/legacy-pattern-components';
import { useRouter } from '@bymason-private/route-provider';
import { useGetJobsStatus, useReadProject } from '@bymason/controller-api-hooks';
import { formatLongDateTimeString } from '@bymason/utils';
import Layout from '../../common/layout';
import { routes } from '../../router';
import BuildStepper, { StepId } from './build-stepper';
import BuildsList from './builds-list';
import { useCheckFeatureFlag } from '../../global-state';

const useStyles = makeStyles(() => ({
  sectionSpacing: {
    marginBottom: 16,
    marginTop: 16,
  },
  heading: {
    marginBottom: 16,
  },
  headingActions: {
    '& > :not(:first-of-type)': {
      marginLeft: 8,
    },
  },
  ratingPopup: {
    position: 'fixed',
    bottom: 0,
    right: 0,
  },
}));

export default function ProjectView({ projectId = '', action, stepId }: ProjectViewProps) {
  const { authToken = '', customerId } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const { navigate } = useRouter();
  const classes = useStyles();
  const [isBuildEnabled] = useCheckFeatureFlag('controller_build');
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [
    getProject,
    projectDetails,
    projectDetailsError,
    projectDetailsBusy,
    projectDetailsStatus,
  ] = useReadProject(authToken);
  const [getBuilds, builds, buildsError, buildsBusy] = useGetJobsStatus(authToken);

  const handleDeployModalOpen = () => {
    setShowDeployModal(true);
  };

  const handleDeployModalClose = () => {
    setShowDeployModal(false);
  };

  const handleNewBuildClick = useCallback(() => {
    navigate(routes.ProjectBuildStepper, { projectId, stepId: 'apps' });
  }, [projectId, navigate]);

  const handleDeploySuccess = useCallback(
    (deploy: ConfigDeployment) => {
      setToastSeverity('success');
      setToastMessage(`${deploy.artifactName} deployment issued to ${deploy.groupName || deploy.groupId}`);
    },
    [setToastMessage, setToastSeverity]
  );

  const handleDeployError = useCallback(
    (errorMessage: string) => {
      if (errorMessage) {
        setToastSeverity('error');
        setToastMessage(errorMessage);
      }
    },
    [setToastSeverity, setToastMessage]
  );

  const handleCreateBuildSuccess = useCallback(() => {
    if (customerId) {
      getBuilds({ customerId });
    }
  }, [customerId, getBuilds]);

  const projectBuilds = useMemo(
    () => builds?.data.filter((build) => build.project === projectDetails?.name),
    [builds, projectDetails?.name]
  );

  useEffect(() => {
    if (projectId && customerId) {
      getProject({ projectId });
      getBuilds({ customerId });
    }
  }, [customerId, getBuilds, getProject, projectId]);

  useEffect(() => {
    const errorMessage =
      projectDetailsStatus === 404
        ? `Sorry, we are unable to retrieve information about the project "${projectDetails?.name || ''}""`
        : projectDetailsError || buildsError || '';
    if (errorMessage) {
      setToastMessage(errorMessage);
      setToastSeverity('error');

      if (projectDetailsStatus === 404) {
        navigate(routes.Projects);
      }
    }
  }, [
    buildsError,
    projectDetails?.name,
    navigate,
    projectDetailsError,
    projectDetailsStatus,
    setToastMessage,
    setToastSeverity,
  ]);

  const detailItems = useMemo<DetailItem[]>(() => {
    const name = projectDetails?.name ?? '';
    const desc = projectDetails?.description ?? '';
    const devices = projectDetails?.deviceFamily ?? 'No devices';
    const api = `${projectDetails?.apiLevel ?? ''}`;
    const createdAt = projectDetails?.createdAt ?? '';

    return [
      { type: 'id', label: 'Identifier', value: name },
      { type: 'description', label: 'Description', value: desc },
      { type: 'devices', label: 'Device', value: devices },
      { type: 'api', label: 'API', value: api },
      { type: 'createdAt', label: 'Created', value: createdAt ? formatLongDateTimeString(createdAt) : '' },
    ];
  }, [projectDetails]);

  return (
    <Layout
      busy={projectDetailsBusy || buildsBusy}
      headerText={projectDetails?.name || ''}
      headerRightAdornment={
        <div className={classes.headingActions}>
          {!action && isBuildEnabled && (
            <Button variant='outlined' onClick={handleDeployModalOpen}>
              Deploy to group
            </Button>
          )}
          {!action && isBuildEnabled && (
            <Button
              data-gtm-id={`Build-in-controller-create-build`}
              variant='outlined'
              onClick={handleNewBuildClick}
            >
              Create new build
            </Button>
          )}
        </div>
      }
      stickyHeader
    >
      <section className={classes.sectionSpacing}>
        <LegacyDetailsList items={detailItems} />
      </section>
      {projectBuilds && !buildsBusy && action === 'build' && (
        <BuildStepper
          projectId={projectId}
          projectName={projectDetails?.name || ''}
          projectBuilds={projectBuilds}
          stepId={stepId}
          onCreateBuildSuccess={handleCreateBuildSuccess}
        />
      )}
      {projectBuilds && !buildsBusy && action === undefined && (
        <BuildsList projectId={projectId} projectBuilds={projectBuilds} isBuildEnabled={isBuildEnabled} />
      )}

      {projectDetails && showDeployModal && (
        <DeployBuildVersionModal
          project={projectDetails}
          buildJobs={projectBuilds || []}
          open={true}
          controllerBuildEnabled={isBuildEnabled}
          onClose={handleDeployModalClose}
          onSuccess={handleDeploySuccess}
          onError={handleDeployError}
        />
      )}
    </Layout>
  );
}

interface ProjectViewProps {
  projectId?: string;
  action?: 'build';
  stepId?: StepId;
}
